/*
 * DATA_RIGHTS: HONEYWELL CONFIDENTIAL AND PROPRIETARY
 * THIS WORK CONTAINS VALUABLE CONFIDENTIAL AND PROPRIETARY
 * INFORMATION. DISCLOSURE, USE OR REPRODUCTION OUTSIDE OF
 * HONEYWELL INTERNATIONAL, INC. IS PROHIBITED EXCEPT AS
 * AUTHORIZED IN WRITING. THIS UNPUBLISHED WORK IS PROTECTED BY
 * THE LAWS OF THE UNITED STATES AND OTHER COUNTRIES. IN THE
 * EVENT OF PUBLICATION, THE FOLLOWING NOTICE SHALL APPLY:
 * Copyright (C) 2018 HONEYWELL INTERNATIONAL, INC., ALL RIGHTS RESERVED.
 *
 * Created by H217483 on 2/18/2019.
 */

import {Checkbox, Dropdown, Input, Label, List} from "semantic-ui-react";
import React from "react";
import {FormattedMessage, injectIntl} from 'react-intl';
import {Icon, Input as InputScuf} from '@scuf/common';
import {browser} from '../utils/Helpers'

/**
 * Component to render tree selection in a dropdown component. The props are:
 * tree - tree hierarchy of items to show in the component. The format of the items is
 *          {
 *              id<Integer>,
 *              name<String>,
 *              selectable<Boolean>,
 *              items: [] - list of children items
 *          }
 * selectedItems - array of IDs of selected items
 * subtreeSelection - does not allow to select items in the same path of tree
 * singleSelection - by defualt the component allows multipleSelection. This property sets single selection mode
 * selectionChanged - callback when selection has changed. Returns an array of IDs
 * placeholder - shows placeholder in input field
 * asDropdown - if true, will render inside of dropdown component
 * search - if passed, the search field will behave as a controlled component
 */
class TreeSelector extends React.Component {
    constructor(props) {
        super(props)

        this.state = {
            open: false,
            search: '',
            searchInputScuf: '',
            expandedItems: [],
        }

        this.toggleItem = this.toggleItem.bind(this)
        this.onClick = this.onClick.bind(this)
        this.onBlur = this.onBlur.bind(this)
        this.searchChanged = this.searchChanged.bind(this)
        this.searchChangedScuf = this.searchChangedScuf.bind(this)
        this.getTree = this.getTree.bind(this)
        this.timeout = 0;
    }

    componentDidMount() {
        if (this.props.tree) {
            this.expandAllItems();
        }
    }

    componentDidUpdate(prevProps) {
        //if initial tree is received in props
        if (this.getTreeHash(prevProps.tree) !== this.getTreeHash(this.props.tree)) {
            //if tree should be expanded on init, extract all ids from tree and add to expandedItems state array
            this.expandAllItems();
        }
    }

    getTreeHash(tree) {
        const hash = ''
        if (tree) {
            const allIdsArrays = tree.map(i => {
                return this.getChildrenItemsIds(i);
            })
            const allIds = [].concat.apply([], allIdsArrays)
            return allIds.join(",")
        }
        return hash
    }

    expandAllItems() {
        if (this.props.initExpandAll) {
            const allIdsArrays = this.props.tree.map(i => {
                return this.getChildrenItemsIds(i);
            })
            const allIds = [].concat.apply([], allIdsArrays)
            this.setState({expandedItems: allIds})
        }
    }

    getSearchValue() {
        return this.props.search != null ? this.props.search : this.state.search
    }

    /**
     * Returns tree of components to render. When search query is not empty, flat list of Component items is returned instead of the tree
     */
    getTree() {
        const result = this.props.tree.map(i => {
                if (this.getSearchValue().length === 0) {
                    return this.getTreeItem(i, null)
                } else {
                    const list = this.getFlatListItem(i, null)
                    if (list.length === 0) {
                        return;
                    }
                    return list
                }
            }
        )
        return result.some(v => v !== undefined) ? <List>{result}</List> : <FormattedMessage key="noresult" id="common.noresults" />
    }

    /**
     * Gets component for displaying in tree hierarchy
     */
    getTreeItem(item, parent) {
        const isExpanded = this.state.expandedItems.indexOf(item.id) > -1
        const hasChildren = item.items && item.items.length > 0

        let icon = <List.Icon/>
        if (hasChildren) {
            icon = <List.Icon name={'angle ' + (isExpanded ? 'down' : 'right')} onClick={() => this.toggleItem(item)}/>
        }

        item.checked = this.props.selectedItems.indexOf(item.id) > -1 || (this.props.subtreeSelection && parent != null && parent.checked)

        let itemComponent = null
        if (item.selectable) {
            if (this.props.singleSelection) {
                itemComponent = <span className={"single selectable-item " + (item.checked ? 'selected' : '')}
                      onClick={() => this.checkItem(item, !item.checked)}>{item.name}</span>
            } else {
                itemComponent = <Checkbox
                    label={item.name}
                    checked={item.checked}
                    onChange={(e, data) => this.checkItem(item, data.checked)}
                    disabled={this.props.subtreeSelection ? (parent && parent.checked) : false}
                    className="selectable-item"/>
            }
        } else {
            itemComponent = <span className="list-item" onClick={() => this.toggleItem(item)}>{item.name}</span>
        }

        let childrenList = null
        if (hasChildren && isExpanded) {
            childrenList = (
                <List.List>
                    {item.items.map(i => this.getTreeItem(i, item))}
                </List.List>
            )
        }

        return <List.Item key={item.id}>
            {icon}
            <List.Content>
                {itemComponent}
                {childrenList}
            </List.Content>
        </List.Item>
    }

    /**
     * Gets component for displaying in a flat list
     */
    getFlatListItem(item, parent) {
        const hasChildren = item.items && item.items.length > 0

        item.checked = this.props.selectedItems.indexOf(item.id) > -1 || (this.props.subtreeSelection && parent != null && parent.checked)

        let itemComponent = null
        if (item.selectable) {
            if (this.props.singleSelection) {
                itemComponent = <span className={"single selectable-item " + (item.checked ? 'selected' : '')}
                                      onClick={() => this.checkItem(item, !item.checked)}>{item.name}</span>
            } else {
                itemComponent = <Checkbox
                    label={item.name}
                    checked={item.checked}
                    onChange={(e, data) => this.checkItem(item, data.checked)}
                    disabled={this.props.subtreeSelection ? (parent && parent.checked) : false}/>
            }
        } else {
            itemComponent = <span>{item.name}</span>
        }

        let resultList = []

        if (item.selectable && item.name.toLowerCase().indexOf(this.getSearchValue().toLowerCase()) > -1) {
            resultList.push(
                <List.Item key={item.id}>
                    <List.Content>
                        {itemComponent}
                    </List.Content>
                </List.Item>
            )
        }

        if (hasChildren) {
            resultList = resultList.concat(...item.items.map(i => this.getFlatListItem(i, item)))
        }

        return resultList
    }

    /**
     * Expands/collapses tree item
     */
    toggleItem(item) {
        const expandedItems = (this.state.expandedItems.indexOf(item.id) < 0) ? [...this.state.expandedItems, item.id] : this.state.expandedItems.filter(s => s !== item.id)
        this.setState({expandedItems: expandedItems})
    }

    /**
     * Check/uncheck item
     */
    checkItem(item, checked) {
        const checkSubtree = this.props.subtreeSelection

        let selectedItems
        if (this.props.singleSelection) {
            if (checked) {
                selectedItems = [item.id]
            } else {
                selectedItems = []
            }
        } else {
            selectedItems = checked ? [...this.props.selectedItems, item.id] : this.props.selectedItems.filter(s => s !== item.id)
        }

        //unselect any child items if subtreeSelection=true
        if (checkSubtree && checked) {
            const itemsToDeselect = this.getChildrenItemsIds(item).filter(i => i !== item.id)
            selectedItems = selectedItems.filter(s => itemsToDeselect.indexOf(s) === -1)
        }

        if (this.props.singleSelection) {
            this.searchChanged(null, {value: ''})
            setTimeout(() => {
                this.setState({open: false})
            }, 5)
        }

        this.props.selectionChanged(selectedItems);
    }

    /**
     * Returns list of ids of children items recursively including id of passed item
     */
    getChildrenItemsIds(item) {
        let items = [item.id]
        if (item.items) {
            items = items.concat(...item.items.map(i => this.getChildrenItemsIds(i)))
        }
        return items
    }

    /**
     * Finds item in hierarchy based on id
     */
    findItem(id, items) {
        for (const item of items) {
            if (item.id === id) {
                return item
            } else if (item.items) {
                const findedItem = this.findItem(id, item.items)
                if (findedItem) {
                    return findedItem
                }
            } else {
                // Continue searching
            }
        }
    }

    /**
     * Gets label component for currently selected values with cross icon for removal
     */
    getLabel(id) {
        const item = this.findItem(id, this.props.tree);
        if (item) {
            return <Label key={id}>
                {item.name}
                {!this.props.singleSelection ?
                    <Icon name='close' size="small" onClick={() => this.checkItem(item, false)}/>
                    :
                    null
                }
            </Label>
        }
    }

    searchChanged(e, value) {
        this.setState({search: value.value})
        this.props.searchChanged(value.value)
    }

    searchChangedScuf(value) {
        this.setState({
            searchInputScuf: value,
        })
        const me = this;
        clearTimeout(this.timeout)
        this.timeout = setTimeout(function() {
            me.searchChanged(null, {value: value})}, 300
        )
    }

    render() {
        const {singleSelection} = this.props
        const list = this.getTree()

        let labels
        let placeholder = this.props.placeholder
        let searchValue = this.getSearchValue()
        if (singleSelection) {
            if (this.props.selectedItems.length > 0) {
                const item = this.findItem(this.props.selectedItems[0], this.props.tree)
                if (item) {
                    placeholder = item.name
                    if (!this.state.open) {
                        searchValue = item.name
                    }
                }
            }
        } else {
            labels = this.props.selectedItems.map(i => this.getLabel(i))
        }

        let error = null;
        if (this.props.error) {
            error = <span className="ui error-message">{this.props.error}</span>
        }

        if(!this.props.asDropdown) {

            return (<div className={this.getClassNames()} onBlur={this.onBlur}>
                    <InputScuf className="tree-dropdown-input"
                               placeholder={placeholder} fluid
                               onChange={this.searchChangedScuf}
                               value={this.state.searchInputScuf}
                               search={true}/>
                        {list}
                </div>)
        }

        return (
            <div className={this.getClassNames()} onBlur={this.onBlur}>

                <Dropdown trigger={<React.Fragment>{labels}<Input className="tree-dropdown-input"
                                                                  placeholder={placeholder} fluid
                                                                  onChange={this.searchChanged}
                                                                  value={searchValue}/></React.Fragment>}
                          fluid
                          onClick={this.onClick}
                          open={this.state.open}
                          disabled={this.props.disabled} >
                    <Dropdown.Menu className="tree-dropdown">
                        {list}
                        {
                            this.props.footer ?
                                <div className="footer">{this.props.footer}</div>
                                :
                                null
                        }
                    </Dropdown.Menu>
                </Dropdown>
                {error}
            </div>
        )
    }

    getClassNames() {
        let result = 'tree-container tree-dropdown-container'
        if (this.props.error) {
            result += ' error'
        }
        return result;
    }

    onClick(e) {
        if (e.target.classList.contains("dropdown") && e.target.classList.contains("icon")) {
            this.setState({open: !this.state.open})
        } else if (e.target.closest(".footer")) {
            this.setState({open: false})
        } else if (e.target.closest(".tree-dropdown-container")) {
            this.setState({open: true})
        } else if (!e.target.closest(".tree-dropdown")) {
            this.setState({open: !this.state.open})
        } else {
            // Do nothing
        }
    }

    onBlur(e) {
        /*
            IE does not support relatedTarget on blur event, so there is no way to detect if onBlur happened inside the Dropdown or outside.
            So this is a workaround in IE resulting that the dropdown will not close when clicked on the outside of it, but at least
            it allows selecting items from the dropdown.
         */
        if (browser.name !== 'ie' && !e.relatedTarget) {
            this.setState({open: false})
        }
    }

}

TreeSelector.defaultProps = {
    tree: [],
    selectedItems: [],
    subtreeSelection: false,
    singleSelection: false,
    selectionChanged: () => {
        return
    },
    searchChanged: () => {
        return
    },
    initExpandAll: true,
    placeholder: null,
    asDropdown: true,
    search: null,
}

export default injectIntl(TreeSelector)